import { Injectable } from "@angular/core";
import { NavTreeNode } from "../../../../interface/tree";
import { cloneDeep } from 'lodash-es';
import { NODE_CONTENT_FIELD } from '../../../../components/tree/tree';
import { escapeTextForBrowser } from "../../../../utils/escape";

/** 节点内容包含关键词 */
const FIT_KEY = "FIT_KEY";
/** 节点的后代节点中包含符合关键词的节点，则节点为搜索结果主干节点 */
const SEARCH_RESULT_MAIN_TRUNK = "SEARCH_RESULT_MAIN_TRUNK";
/** 子节点关系备份 */
const CHILDREN_BACKUP = "CHILDREN_BACKUP";

/**
 * 导航树自定义搜索服务
 * @remarks
 * 按照be组对搜索栏的要求定制，弃用树表格组件的默认实现
 * 本实现将作为导航树组件的默认搜索服务
 */
@Injectable()
export class NavTreeSearchService {

  /**
   * 搜索
   * @param flatNodes 扁平化节点数组
   * @param key 关键词
   * @returns 搜索结果
   */
  public search(flatNodes: NavTreeNode[], key: string): NavTreeNode[] {
    if (!key) {
      return;
    }
    key = key.toLowerCase();
    this.initAllNode(flatNodes);
    const fitNodes = flatNodes.filter(node => ('' + node.data.name).toLowerCase().indexOf(key) > -1);
    this.highLightFitNodes(fitNodes, key);
    for (const node of fitNodes) {
      node[FIT_KEY] = true;
    }
    const mainTrunkNodes = this.getAllParents(fitNodes);
    for (const node of mainTrunkNodes) {
      node[SEARCH_RESULT_MAIN_TRUNK] = true;
      node.expanded = true;  // 所有符合条件节点的祖先节点都应该展开
    }
    return this.buildSearchResultTree(flatNodes);
  }

  /** 高亮搜索关键词 */
  private highLightFitNodes(nodes: NavTreeNode[], key: string): void {
    for (const node of nodes) {
      const name = node.data.name;
      const keyIdx = name.toLowerCase().indexOf(key);
      if (keyIdx < 0) {
        continue;
      }
      let prefix = name.substring(0, keyIdx) || '';
      let keyStr = name.substring(keyIdx, keyIdx + key.length);
      let suffix = name.substring(keyIdx + key.length) || '';
      prefix = escapeTextForBrowser(prefix);
      keyStr = escapeTextForBrowser(keyStr);
      suffix = escapeTextForBrowser(suffix);
      const contentHTML = prefix + `<span style="background-color: #FFFF00;">${keyStr}</span>` + suffix;
      node.data[NODE_CONTENT_FIELD] = contentHTML;
    }
  }

  /**
   * 复制节点数据并将结果展平为一个一维数组
   * @param nodes 原节点数据
   * @returns 扁平化节点数组
   */
  public cloneDeepAndFlatNodes(nodes: NavTreeNode[]): NavTreeNode[] {
    const cloneNodes: NavTreeNode[] = cloneDeep(nodes) || [];
    // 在展平之前先记录好每个节点的父节点引用
    const result: NavTreeNode[] = [];
    for (const node of cloneNodes) {
      traverseTree(node, (node, parent) => {
        result.push(node);
        node.parent = parent;  // 生成父节点关系备份
        node.children = node.children || [];  // 生成子节点关系备份
        node[CHILDREN_BACKUP] = [ ...node.children ];
      }, null);
    }
    return result;
  }

  /** 初始化所有节点 */
  private initAllNode(flatNodes: NavTreeNode[]): void {
    for (const node of flatNodes) {
      if (node) {
        node.expanded = false;
        node[FIT_KEY] = false;
        node[SEARCH_RESULT_MAIN_TRUNK] = false;
        const name = node.data && node.data.name || '';
        node.data[NODE_CONTENT_FIELD] = escapeTextForBrowser(name);
        node.children = [];
      }
    }
  }

  /** 获取节点的所有祖先节点（不包含本节点） */
  private getAllParents(nodes: NavTreeNode[]): NavTreeNode[] {
    const set = new Set<NavTreeNode>();
    for (const node of nodes) {
      let parent = node.parent;
      while (!!parent) {
        set.add(parent);
        parent = parent.parent;
      }
    }
    return Array.from(set);
  }

  /** 根据标识组织搜索结果树 */
  private buildSearchResultTree(flatNodes: NavTreeNode[]): NavTreeNode[] {
    // 获取符合条件的根节点集合
    const roots = flatNodes.filter(n => !n.parent && ( n[FIT_KEY] || n[SEARCH_RESULT_MAIN_TRUNK] ));
    // 遍历所有节点，通过备份的子节点关系重建树，并减去完全不相干的枝
    const prune = (node: NavTreeNode) => {
      // 首先根据备份的子节点关系恢复children字段
      node.children = [ ...node[CHILDREN_BACKUP] ];
      if (node[FIT_KEY]) {
        return;  // 如果节点符合关键词，则保留其全部的子节点
      }
      if (node[SEARCH_RESULT_MAIN_TRUNK]) {
        // 如果节点是符合条件节点的祖先节点，但是本身不符合条件，则减去其所有与搜索结果无关的子节点
        let length = node.children.length;
        for (let i = 0; i < length; i++) {
          const child = node.children[i];
          if ( !child[FIT_KEY] && !child[SEARCH_RESULT_MAIN_TRUNK] ) {
            node.children.splice(i, 1);
            --length;
            --i;
          }
        }
        return;
      }
    };
    for (const node of flatNodes) {
      prune(node);
    }
    return roots;
  }

}

/**
 * 递归遍历树
 * @param node 树节点
 * @param handleNode 处理每一个树节点
 */
function traverseTree(
  node: NavTreeNode,
  handleNode: (node: NavTreeNode, parent: NavTreeNode) => void,
  parent: NavTreeNode = null
): void {
  if (!node) {
    return;
  }
  handleNode(node, parent);
  if (node.children && node.children.length > 0) {
    for (const child of node.children) {
      traverseTree(child, handleNode, node);
    }
  }
}
